import { useQuery } from '@tanstack/react-query';
import type { TenantUserMetricsResponseDto } from '@/generated/korza-api-client/types.gen';
import { useTenant } from '@/hooks/use-tenant';
import { useWithPrevious } from '@/hooks/use-with-previous';
import {
  appDownloadsChartQuery,
  appDownloadsCumulativeChartQuery,
  appSessionsChartQuery,
  dataSufficiencyQuery,
  installedVersionsQuery,
  tabRange,
  userMetricsQuery,
  type AnalyticsTabParams,
} from '@/hooks/analytics/analytics-queries';

/**
 * Queries of the Usuários tab, fetched only while it is open (ANLT-01), with
 * the previous period of `/metrics/users` (Downloads variation, ANLT-06).
 */
export function useUsersTabData(params: AnalyticsTabParams) {
  const tenant = useTenant();
  const range = { ...tabRange(params), preset: params.preset };
  const { granularity, enabled } = params;

  const users = useWithPrevious<TenantUserMetricsResponseDto>(
    (r) => ({ ...userMetricsQuery(tenant.slug, r), enabled }),
    range
  );
  const appDownloadsChart = useQuery({
    ...appDownloadsChartQuery(tenant.slug, range, granularity),
    enabled,
  });
  const appSessionsChart = useQuery({
    ...appSessionsChartQuery(tenant.slug, range, granularity),
    enabled,
  });
  const appDownloadsCumulativeChart = useQuery({
    ...appDownloadsCumulativeChartQuery(tenant.slug, range, granularity),
    enabled,
  });
  const installedVersions = useQuery({
    ...installedVersionsQuery(tenant.slug, range),
    enabled,
  });
  const dataSufficiency = useQuery({
    ...dataSufficiencyQuery(tenant.slug, range),
    enabled,
  });

  return {
    userMetrics: users.current,
    downloadsDelta: users.delta((d) => d.downloads),
    appDownloadsChart,
    appSessionsChart,
    appDownloadsCumulativeChart,
    installedVersions,
    dataSufficiency,
  };
}
